import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha } from "@mui/material/styles";
import theme from "../../theme";
import { IdeaSource } from "../../types";
// Get colors from theme
const accentColor = theme.palette.primary.main;
const dividerColor = theme.palette.divider;
const humanColor = theme.palette.warning.main;
const machineColor = theme.palette.primary.main;
const primaryTextColor = theme.palette.text.primary;
const secondaryTextColor = theme.palette.text.secondary;
const surfaceColor = theme.palette.background.paper;
// Rendered via `renderToStaticMarkup` outside a normal React commit, so Emotion's
// `useInsertionEffect`-based styling never fires here — use plain inline styles.
// #region: CSSProperties
const badgeStyle = {
    flexShrink: 0,
    padding: "1px 6px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    textTransform: "uppercase",
};
const cardStyle = {
    maxWidth: 380,
    // The cap keeps a tall stack of ideas inside the 500px chart box the tooltip is
    // positioned against, and hands the overflow to the scrolling list below.
    maxHeight: "min(55vh, 360px)",
    display: "flex",
    flexDirection: "column",
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    lineHeight: 1.45,
    // The echarts tooltip container this renders into sets `white-space: nowrap`.
    whiteSpace: "normal",
    color: primaryTextColor,
    background: surfaceColor,
    border: `1px solid ${alpha(accentColor, 0.35)}`,
    borderRadius: 10,
    boxShadow: `0 8px 24px ${alpha(primaryTextColor, 0.16)}`,
    overflow: "hidden",
};
const coordinateStyle = {
    color: secondaryTextColor,
    fontSize: 12,
};
const feedbackStyle = {
    marginTop: 4,
    marginLeft: 26,
    paddingLeft: 8,
    borderLeft: `2px solid ${alpha(accentColor, 0.3)}`,
    color: secondaryTextColor,
    overflowWrap: "anywhere",
};
const headerStyle = {
    flexShrink: 0,
    padding: "8px 12px",
    background: alpha(accentColor, 0.06),
    borderBottom: `1px solid ${dividerColor}`,
};
const indexStyle = {
    color: secondaryTextColor,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
};
const ideaStyle = {
    minWidth: 0,
    fontWeight: 600,
    overflowWrap: "anywhere",
};
const listStyle = {
    minHeight: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
};
const humanBadgeStyle = { ...badgeStyle, background: alpha(humanColor, 0.14), color: humanColor };
const machineBadgeStyle = { ...badgeStyle, background: alpha(machineColor, 0.14), color: machineColor };
// #endregion: Types
// Objective names are long enough to wrap, and a plain space would let the score break
// onto a line of its own.
const NBSP = "\u00A0";
// Joins with NBSP (see above) and rounds the score to one decimal for a compact axis line.
const formatCoordinateLine = (label, value) => `${label}${NBSP}${value.toFixed(1)}`;
// AI is already correctly cased as an acronym. Only HUMAN needs title-casing for display.
const formatSource = (source) => (source === IdeaSource.HUMAN ? "Human" : source);
// The backend joins one explanation per objective with " | ", so each piece gets its
// own quoted line rather than wrapping into a single paragraph.
const feedbackLines = (feedback) => feedback
    .split("|")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
const IdeasChartTooltip = ({ x, y, xLabel, yLabel, points }) => {
    const coordinates = [
        { axis: "x", label: xLabel, score: x },
        { axis: "y", label: yLabel, score: y },
    ];
    return (_jsxs("div", { "data-testid": "ideas_chart_tooltip-card-div", style: cardStyle, children: [_jsx("div", { style: headerStyle, children: _jsx("div", { style: coordinateStyle, children: coordinates.map(({ axis, label, score }) => (_jsx("div", { children: formatCoordinateLine(label, score) }, axis))) }) }), _jsx("div", { style: listStyle, children: points.map((point, index) => (_jsxs("div", { style: {
                        padding: "10px 12px",
                        borderTop: index > 0 ? `1px solid ${dividerColor}` : undefined,
                    }, children: [_jsxs("div", { style: { display: "flex", alignItems: "baseline", gap: 6 }, children: [_jsx("span", { style: indexStyle, children: `#${point.ideaNumber}` }), _jsx("span", { style: point.source === IdeaSource.HUMAN ? humanBadgeStyle : machineBadgeStyle, children: formatSource(point.source) }), _jsx("span", { style: ideaStyle, children: point.idea })] }), feedbackLines(point.feedback).map((line) => (_jsx("div", { style: feedbackStyle, children: line }, line)))] }, `${point.ideaNumber}-${point.source}`))) })] }));
};
export default IdeasChartTooltip;
